import { computed, onMounted, ref, shallowRef, unref, watch } from 'vue';
import { debounce } from 'lodash';
import { escapeSql } from '@rdrive/helper-sql';
import { hasNewAssigneeColumns, teamAssigneeColumn } from '../data/filters.js';

// The Overview and Drawings pages' headline cards (IA_ABSORPTION_PLAN Phase 4 step 13; was
// components/card-arrays/TotalAndAssignedCards.vue, whose nine query strings these are): the three
// counts over `filteredIssues`, their weekly sparklines (issues created per week, last 12 weeks)
// with the last week's change, and the two "assigned to…" switch filters. The pages render
// @rdrive/plugin-total-cards' TotalAndAssignedCards with the opt-in `sparklines` (the spark row).
//
// Filter registration ids — each keys a filter registry AND a DuckDB temp view `filter_view_<id>`,
// which is global to the connection, so the two contexts must never share one. They did: the old
// component registered `assignedToYourTeamsCardFilterContext` / `assignedToMeCardFilterContext` on
// BOTH contexts, so a switch's two filters fought over one view (step 13 fix: the status-history
// context gets its own ids).
const TEAMS_FILTER_ID = 'assignedToYourTeamsCardFilterContext';
const ME_FILTER_ID = 'assignedToMeCardFilterContext';
const TEAMS_HISTORY_FILTER_ID = 'assignedToYourTeamsCardStatusHistory';
const ME_HISTORY_FILTER_ID = 'assignedToMeCardStatusHistory';

const NO_SPARK = { data: [], labels: [], percentageChange: null };

// One weekly query's rows → the card's sparkline: the counts, the x-axis labels (MM/DD of the
// week's start in local time — the first, the last and every Nth week, N = 2 above 4 weeks and
// ceil(weeks / 5) above 8) and the last week's change against the week before, in % to one
// decimal (100 when the week before had none, 0 when neither had any, null for < 2 weeks). A
// single week is drawn as a flat line (the card needs two points).
const weeklySeries = (rows, countKey) => {
  const counts = rows.map(row => Number(row.toJSON()[countKey]));
  const labels = rows.map((row, index) => {
    const date = new Date(row.toJSON().week_start);
    const isFirst = index === 0;
    const isLast = index === rows.length - 1;
    let labelInterval = 1;
    if (rows.length > 8) labelInterval = Math.ceil(rows.length / 5);
    else if (rows.length > 4) labelInterval = 2;
    const showLabel = isFirst || isLast || (index % labelInterval === 0);
    if (showLabel) {
      return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
    }
    return '';
  });

  let series;
  if (counts.length === 1) series = { data: [counts[0], counts[0]], labels: [labels[0], ''] };
  else if (counts.length > 0) series = { data: counts, labels };
  else series = { data: [], labels: [] };

  let percentageChange = null;
  if (counts.length >= 2) {
    const currentWeek = counts[counts.length - 1];
    const previousWeek = counts[counts.length - 2];
    if (previousWeek > 0) {
      percentageChange = Math.round(((currentWeek - previousWeek) / previousWeek) * 1000) / 10;
    } else if (currentWeek > 0) {
      percentageChange = 100;
    } else {
      percentageChange = 0;
    }
  }
  return { ...series, percentageChange };
};

/**
 * @param {object} deps  each a value, a ref or a computed
 * @param {object} deps.conn                  DuckDBService's gated connection
 * @param {object} deps.filterContext         the issues context (`useDashboard().filters.issues`)
 * @param {object} deps.statusHistoryContext  the status-history context
 * @param {string} deps.userId                the current user's id
 * @param {boolean} deps.dataLoaded           the loader's flag
 * @returns {{ totalItems, totalAssignedToYourTeams, totalAssignedToYou, sparklines,
 *   filterByMyTeams, filterByMe, handleFilterChange }}  refs, a computed and the cards'
 *   `filterChange` handler — bound by the page onto TotalAndAssignedCards
 */
export function useIssueTotals({ conn, filterContext, statusHistoryContext, userId, dataLoaded }) {
  const db = () => unref(conn);
  const issues = () => unref(filterContext);
  const history = () => unref(statusHistoryContext);
  const uid = () => unref(userId);
  const loaded = () => unref(dataLoaded);

  const totalItems = ref(0);
  const totalAssignedToYourTeams = ref(0);
  const totalAssignedToYou = ref(0);

  // The weekly sparklines (a failed query empties `data`/`percentageChange` and keeps `labels`)
  const issuesSpark = shallowRef(NO_SPARK);
  const teamSpark = shallowRef(NO_SPARK);
  const userSpark = shallowRef(NO_SPARK);
  const sparklines = computed(() => ({
    totalItems: issuesSpark.value,
    totalAssignedToYourTeams: teamSpark.value,
    totalAssignedToYou: userSpark.value,
  }));

  // Filter states for switches (the cards' v-models)
  const filterByMyTeams = ref(false);
  const filterByMe = ref(false);

  // Precomputed team IDs for the current user (used for "Assigned to Your Teams")
  const myTeamIds = ref([]);

  const isMounted = ref(false);

  // Precompute team IDs in a standalone query
  const computeMyTeamIds = async () => {
    try {
      const teamsQuery = `
      SELECT Id
      FROM team_users
      WHERE users::VARCHAR LIKE '%' || '${escapeSql(uid())}' || '%'
    `;
      const result = await db().query(teamsQuery);
      const rows = result.toArray();
      myTeamIds.value = rows.map(row => row.toJSON().Id);
    } catch (error) {
      console.error("Error computing team IDs:", error);
      myTeamIds.value = [];
    }
  };

  const buildIdsList = (ids) => {
    return ids.length > 0 ? ids.map(id => `'${escapeSql(id)}'`).join(',') : "''";
  };

  // The "assigned to me" SQL condition, by API version (read live from the issues context's last
  // detection). New API: issueUserAssignees may contain comma-separated user IDs (e.g. "2,3").
  // Legacy API (or no user-assignee column): not supported — FALSE, so the count is 0.
  const buildAssignedToMeCondition = (alias = 'fi') => {
    if (hasNewAssigneeColumns(issues())) {
      return `list_contains(string_split(${alias}.issueUserAssignees, ','), '${escapeSql(uid())}')`;
    }
    return 'FALSE';
  };

  // The same over the raw issues table
  const buildAssignedToMeConditionRaw = (alias = 'i') => {
    if (hasNewAssigneeColumns(issues())) {
      return `list_contains(string_split(${alias}.IssueUserAssignees, ','), '${escapeSql(uid())}')`;
    }
    return 'FALSE';
  };

  const computeWeeklyIssuesData = async () => {
    if (!isMounted.value || !loaded()) return;

    try {
      const weeklyQuery = `
      WITH issue_creation AS (
        SELECT
          SubItemId as issue_id,
          MIN(CreatedDate) as creation_date
        FROM status_history
        GROUP BY SubItemId
      ),
      weekly_counts AS (
        SELECT
          DATE_TRUNC('week', creation_date) as week_start,
          COUNT(DISTINCT ic.issue_id) as issue_count
        FROM issue_creation ic
        INNER JOIN filteredIssues fi ON ic.issue_id = fi.id
        WHERE creation_date >= CURRENT_DATE - INTERVAL '12 weeks'
        GROUP BY DATE_TRUNC('week', creation_date)
        ORDER BY week_start
      )
      SELECT
        week_start,
        issue_count
      FROM weekly_counts
    `;

      const result = await db().query(weeklyQuery);
      issuesSpark.value = weeklySeries(result.toArray(), 'issue_count');
    } catch (error) {
      console.error("Error computing weekly issues data:", error);
      issuesSpark.value = { ...issuesSpark.value, data: [], percentageChange: null };
    }
  };

  const computeWeeklyTeamAssignmentsData = async () => {
    if (!isMounted.value || !loaded()) return;

    try {
      const teamIdsList = buildIdsList(myTeamIds.value);
      const weeklyQuery = `
      WITH issue_creation AS (
        SELECT
          SubItemId as issue_id,
          MIN(CreatedDate) as creation_date
        FROM status_history
        GROUP BY SubItemId
      ),
      weekly_counts AS (
        SELECT
          DATE_TRUNC('week', ic.creation_date) as week_start,
          COUNT(DISTINCT ic.issue_id) as assignment_count
        FROM issue_creation ic
        INNER JOIN filteredIssues fi ON ic.issue_id = fi.id
        WHERE ic.creation_date >= CURRENT_DATE - INTERVAL '12 weeks'
          AND fi.issueAssignee IN (${teamIdsList})
        GROUP BY DATE_TRUNC('week', ic.creation_date)
        ORDER BY week_start
      )
      SELECT week_start, assignment_count FROM weekly_counts
    `;

      const result = await db().query(weeklyQuery);
      teamSpark.value = weeklySeries(result.toArray(), 'assignment_count');
    } catch (error) {
      console.error("Error computing weekly team assignments data:", error);
      teamSpark.value = { ...teamSpark.value, data: [], percentageChange: null };
    }
  };

  const computeWeeklyUserAssignmentsData = async () => {
    if (!isMounted.value || !loaded()) return;

    try {
      const assignedToMeCondition = buildAssignedToMeCondition('fi');
      const weeklyQuery = `
      WITH issue_creation AS (
        SELECT
          SubItemId as issue_id,
          MIN(CreatedDate) as creation_date
        FROM status_history
        GROUP BY SubItemId
      ),
      weekly_counts AS (
        SELECT
          DATE_TRUNC('week', ic.creation_date) as week_start,
          COUNT(DISTINCT ic.issue_id) as assignment_count
        FROM issue_creation ic
        INNER JOIN filteredIssues fi ON ic.issue_id = fi.id
        WHERE ic.creation_date >= CURRENT_DATE - INTERVAL '12 weeks'
          AND ${assignedToMeCondition}
        GROUP BY DATE_TRUNC('week', ic.creation_date)
        ORDER BY week_start
      )
      SELECT week_start, assignment_count FROM weekly_counts
    `;

      const result = await db().query(weeklyQuery);
      userSpark.value = weeklySeries(result.toArray(), 'assignment_count');
    } catch (error) {
      console.error("Error computing weekly user assignments data:", error);
      userSpark.value = { ...userSpark.value, data: [], percentageChange: null };
    }
  };

  const computeCounts = async () => {
    if (isMounted.value && loaded()) {
      await computeMyTeamIds();

      const teamIdsList = buildIdsList(myTeamIds.value);
      const assignedToMeCondition = buildAssignedToMeCondition('fi');

      const combinedQuery = `
      SELECT
        COUNT(DISTINCT fi.id) AS TotalItems,
        COUNT(DISTINCT CASE WHEN fi.issueAssignee IN (${teamIdsList}) THEN fi.id END) AS AssignedToMyTeams,
        COUNT(DISTINCT CASE WHEN ${assignedToMeCondition} THEN fi.id END) AS AssignedToMe
      FROM filteredIssues fi
    `;

      try {
        const result = await db().query(combinedQuery);
        const row = result.toArray()[0];

        if (!row) {
          totalItems.value = 0;
          totalAssignedToYourTeams.value = 0;
          totalAssignedToYou.value = 0;
          return;
        }

        const jsonRow = row.toJSON();

        totalItems.value = Number(jsonRow.TotalItems);
        totalAssignedToYourTeams.value = Number(jsonRow.AssignedToMyTeams);
        totalAssignedToYou.value = Number(jsonRow.AssignedToMe);

        await computeWeeklyIssuesData();
        await computeWeeklyTeamAssignmentsData();
        await computeWeeklyUserAssignmentsData();

      } catch (error) {
        console.error("Error executing combined query:", error);
        totalItems.value = 0;
        totalAssignedToYourTeams.value = 0;
        totalAssignedToYou.value = 0;
      }
    }
  };

  // Each switch filters BOTH contexts, each under its own id. The issues filter is started
  // without awaiting it (as the store commit it replaces did); the status-history one is awaited.
  const filterByAssignedToMyTeams = async (selected) => {
    if (selected) {
      try {
        const teamIdsList = buildIdsList(myTeamIds.value);
        const issuesQuery = `
        SELECT DISTINCT fi.id
        FROM filteredIssues fi
        WHERE fi.issueAssignee IN (${teamIdsList})
      `;
        const result = await db().query(issuesQuery);
        const rows = result.toArray();
        const assignedToYourTeamFilter = rows.map(row => row.toJSON().id);
        issues().updateFilter(TEAMS_FILTER_ID, assignedToYourTeamFilter);

        const teamAssigneeCol = teamAssigneeColumn(issues());
        const statusHistoryQuery = `
        SELECT DISTINCT sht.subItemId, sht.validFrom, sht.statusId
        FROM status_history_temporal sht
        INNER JOIN issues i ON sht.subItemId = i.Id
        WHERE i.${teamAssigneeCol} IN (${teamIdsList})
      `;
        await history().updateFilterWithQuery(TEAMS_HISTORY_FILTER_ID, statusHistoryQuery);
      } catch (error) {
        console.error("Error filtering by assigned to your teams:", error);
      }
    } else {
      issues().removeFilter(TEAMS_FILTER_ID);
      await history().removeFilter(TEAMS_HISTORY_FILTER_ID);
    }
  };

  const filterByAssignedToMe = async (selected) => {
    if (selected) {
      try {
        const assignedToMeCondition = buildAssignedToMeCondition('fi');
        const issuesQuery = `
        SELECT DISTINCT fi.id
        FROM filteredIssues fi
        WHERE ${assignedToMeCondition}
      `;
        const result = await db().query(issuesQuery);
        const rows = result.toArray();
        const assignedToMeFilter = rows.map(row => row.toJSON().id);
        issues().updateFilter(ME_FILTER_ID, assignedToMeFilter);

        const assignedToMeConditionRaw = buildAssignedToMeConditionRaw('i');
        const statusHistoryQuery = `
        SELECT DISTINCT sht.subItemId, sht.validFrom, sht.statusId
        FROM status_history_temporal sht
        INNER JOIN issues i ON sht.subItemId = i.Id
        WHERE ${assignedToMeConditionRaw}
      `;
        await history().updateFilterWithQuery(ME_HISTORY_FILTER_ID, statusHistoryQuery);
      } catch (error) {
        console.error("Error filtering by assigned to me:", error);
      }
    } else {
      issues().removeFilter(ME_FILTER_ID);
      await history().removeFilter(ME_HISTORY_FILTER_ID);
    }
  };

  // The cards' `filterChange` ({ id, selected }, id = the card's identifier)
  const handleFilterChange = ({ id, selected }) => {
    if (id === 'totalAssignedToYourTeams') {
      filterByMyTeams.value = selected;
      filterByAssignedToMyTeams(selected);
    } else if (id === 'totalAssignedToYou') {
      filterByMe.value = selected;
      filterByAssignedToMe(selected);
    }
  };

  const recomputeCounts = debounce(() => {
    computeCounts();
  }, 100);

  // Every issues-context tick (any chart, the FAB, these switches) recounts
  watch(
    () => issues().filtersLastUpdated,
    () => {
      if (isMounted.value) {
        recomputeCounts();
      }
    },
    { deep: true }
  );

  onMounted(() => {
    recomputeCounts();
    isMounted.value = true;
  });

  watch(
    loaded,
    (isLoaded) => {
      if (isLoaded) {
        recomputeCounts();
      }
    },
    { immediate: true }
  );

  return {
    totalItems,
    totalAssignedToYourTeams,
    totalAssignedToYou,
    sparklines,
    filterByMyTeams,
    filterByMe,
    handleFilterChange,
  };
}
